import React, { useState } from 'react'
import { supabase } from '@/lib/supabase'
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
  Drawer,
  Card,
  Spinner,
  Flex,
  Button,
  Tabs,
  Table,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  Alert,
} from '@chakra-ui/react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronUp,
  ChevronDown,
  X,
  Trash2,
  Calendar,
  Clock,
  Users,
  FileText,
  Play,
  Square,
  Plus,
  GripVertical,
  Pencil,
  MoreVertical,
  Info,
  Send,
  Mail,
  CheckCircle,
  FileSignature,
  Vote,
  AlertTriangle,
  Download,
  ListChecks,
  FileDown,
  UserCheck,
  Video,
  Copy,
} from 'lucide-react'
import { useMeeting, useDeleteMeeting, useStartMeeting, useCloseMeeting, useUpdateMeeting, useIndivisionAlerts, useValidateAgenda, useVideoRoom, useCreateVideoRoom, useDeleteVideoRoom } from '../hooks'
import { useResolutions, useDeleteResolution, useReorderResolutions } from '../hooks/useResolutions'
import { useOwnersPaginated } from '@/features/owners/hooks/useOwners'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useAgency } from '@/features/agencies/hooks/useAgency'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { EditableField } from '@/shared/components/EditableField'
import { EditableSelectField } from '@/shared/components/EditableSelectField'
import { ResolutionFormModal } from './ResolutionFormModal'
import { LiveAgPiloting } from './LiveAgPiloting'
import { meetingsToaster as toaster } from '../pages/MeetingsLayout'
import { downloadVPCForm, type VPCOwnerData, type VPCUnitData } from '../services/generateVPCForm'
import { createMeetingToken, buildRoomUrlWithToken } from '../services/dailyService'
import { downloadConvocationLetter, type ConvocationOwnerData, type ConvocationAgencyData, type ConvocationResidenceData, type ConvocationGestionnaireData } from '../services/generateConvocationLetter'
import { downloadPouvoir, type PouvoirOwnerData, type PouvoirResidenceData } from '../services/generatePouvoir'
import {
  MEETING_STATUS_LABELS,
  MEETING_STATUS_COLORS,
  MEETING_TYPE_LABELS,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_COLORS,
  CONVOCATION_STATUS_LABELS,
  CONVOCATION_STATUS_COLORS,
  type Meeting,
  type Resolution,
  type ConvocationStatus,
} from '../types'

interface MeetingDetailDrawerProps {
  meetingId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function MeetingDetailDrawer({
  meetingId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious,
  hasNext,
}: MeetingDetailDrawerProps) {
  const { data: meeting, isLoading, error } = useMeeting(meetingId ?? undefined)
  const { data: resolutions } = useResolutions(meetingId ?? undefined)
  const deleteMeeting = useDeleteMeeting()
  const startMeeting = useStartMeeting()
  const closeMeeting = useCloseMeeting()
  const validateAgenda = useValidateAgenda()
  const updateMeeting = useUpdateMeeting()

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [enablingVideo, setEnablingVideo] = useState(false)
  const [activeTab, setActiveTab] = useState('info')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuJustClosed, setMenuJustClosed] = useState(false)
  const [isPilotingOpen, setIsPilotingOpen] = useState(false)

  const isEditable = meeting?.status === 'SCHEDULED'

  const handleDelete = async () => {
    if (!meetingId) return

    try {
      await deleteMeeting.mutateAsync(meetingId)
      toaster.create({
        title: 'AG supprimée',
        description: 'L\'assemblée générale a été supprimée',
        type: 'success',
      })
      onClose()
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de supprimer l\'AG',
        type: 'error',
      })
    }
  }

  const handleValidateAgenda = async () => {
    if (!meetingId) return

    try {
      await validateAgenda.mutateAsync(meetingId)
      toaster.create({
        title: 'Ordre du jour validé',
        description: 'Les résolutions sont maintenant en mode vote et l\'AG est prête pour les convocations.',
        type: 'success',
      })
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de valider l\'ordre du jour',
        type: 'error',
      })
    }
  }

  const handleStartMeeting = async () => {
    if (!meetingId) return

    try {
      await startMeeting.mutateAsync(meetingId)
      toaster.create({
        title: 'AG démarrée',
        description: 'L\'assemblée générale est maintenant en cours',
        type: 'success',
      })
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de démarrer l\'AG',
        type: 'error',
      })
    }
  }

  const handleCloseMeeting = async () => {
    if (!meetingId) return

    try {
      await closeMeeting.mutateAsync(meetingId)
      toaster.create({
        title: 'AG clôturée',
        description: 'L\'assemblée générale est maintenant clôturée',
        type: 'success',
      })
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de clôturer l\'AG',
        type: 'error',
      })
    }
  }

  const handleEnableVideo = async () => {
    if (!meetingId) return

    setEnablingVideo(true)
    try {
      await updateMeeting.mutateAsync({
        id: meetingId,
        data: { video_enabled: true } as any,
      })
      toaster.create({
        title: 'Visioconférence activée',
        description: 'La visioconférence est maintenant activée pour cette AG.',
        type: 'success',
      })
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible d\'activer la visioconférence',
        type: 'error',
      })
    } finally {
      setEnablingVideo(false)
    }
  }

  const handleInteractOutside = (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    // Don't close drawer if menu is open or just closed
    if (isMenuOpen || menuJustClosed) return

    const target = e.detail.originalEvent.target as HTMLElement
    const meetingCard = target.closest('[data-meeting-card]') as HTMLElement
    const menuElement = target.closest('[data-scope="menu"]') as HTMLElement

    if (meetingCard || menuElement) return
    onClose()
  }

  return (
    <>
      <Drawer.Root
        open={isOpen}
        placement="end"
        size="lg"
        modal={false}
        onInteractOutside={handleInteractOutside}
      >
        <Drawer.Positioner pointerEvents="none">
          <Drawer.Content maxW="900px" pointerEvents="auto">
            {/* Header */}
            <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
              <Flex align="center" w="full" gap={3}>
                {/* Navigation arrows */}
                <HStack gap={1} flexShrink={0}>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={onNavigatePrevious}
                    disabled={!hasPrevious}
                    aria-label="Précédent"
                  >
                    <ChevronUp size={18} />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={onNavigateNext}
                    disabled={!hasNext}
                    aria-label="Suivant"
                  >
                    <ChevronDown size={18} />
                  </IconButton>
                </HStack>

                {/* Title */}
                <Box flex={1} minW={0}>
                  {isLoading ? (
                    <Spinner size="sm" />
                  ) : meeting ? (
                    <VStack align="start" gap={1}>
                      <HStack gap={2}>
                        <Badge
                          colorPalette={MEETING_STATUS_COLORS[meeting.status] || 'gray'}
                          variant="subtle"
                        >
                          {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
                        </Badge>
                        {meeting.meeting_type && (
                          <Badge variant="outline" colorPalette="gray">
                            {MEETING_TYPE_LABELS[meeting.meeting_type]}
                          </Badge>
                        )}
                      </HStack>
                      <Heading size="md" lineClamp={1}>
                        {meeting.title}
                      </Heading>
                    </VStack>
                  ) : null}
                </Box>

                {/* Actions menu */}
                <HStack gap={2} flexShrink={0}>
                  {/* Bouton Piloter l'AG - visible pour CONVOKED ou LIVE */}
                  {meeting && (meeting.status === 'CONVOKED' || meeting.status === 'LIVE') && (
                    <Button
                      size="sm"
                      colorPalette="brand"
                      onClick={() => setIsPilotingOpen(true)}
                    >
                      <Play size={14} />
                      {meeting.status === 'LIVE' ? 'Reprendre le pilotage' : 'Piloter l\'AG'}
                    </Button>
                  )}
                  {meeting && (
                    <MenuRoot
                      onOpenChange={(e) => {
                        setIsMenuOpen(e.open)
                        if (!e.open) {
                          setMenuJustClosed(true)
                          setTimeout(() => setMenuJustClosed(false), 300)
                        }
                      }}
                    >
                      <MenuTrigger asChild>
                        <IconButton variant="ghost" size="sm" aria-label="Actions">
                          <MoreVertical size={18} />
                        </IconButton>
                      </MenuTrigger>
                      <MenuPositioner>
                        <MenuContent minW="200px" zIndex={9999}>
                          {meeting.status === 'SCHEDULED' && (
                            <MenuItem
                              value="validate-agenda"
                              onClick={handleValidateAgenda}
                              disabled={validateAgenda.isPending}
                            >
                              <HStack gap={2}>
                                <ListChecks size={16} />
                                <Text>Valider l'ordre du jour</Text>
                              </HStack>
                            </MenuItem>
                          )}
                          {meeting.status === 'CONVOKED' && (
                            <MenuItem
                              value="start"
                              onClick={handleStartMeeting}
                              disabled={startMeeting.isPending}
                            >
                              <HStack gap={2}>
                                <Play size={16} />
                                <Text>Démarrer l'AG</Text>
                              </HStack>
                            </MenuItem>
                          )}
                          {meeting.status === 'LIVE' && (
                            <MenuItem
                              value="close"
                              onClick={handleCloseMeeting}
                              disabled={closeMeeting.isPending}
                            >
                              <HStack gap={2}>
                                <Square size={16} />
                                <Text>Clôturer l'AG</Text>
                              </HStack>
                            </MenuItem>
                          )}
                          {/* Activer la visioconférence si elle n'est pas activée */}
                          {isEditable && !meeting.video_enabled && (
                            <MenuItem
                              value="enable-video"
                              onClick={handleEnableVideo}
                              disabled={enablingVideo}
                            >
                              <HStack gap={2}>
                                <Video size={16} />
                                <Text>{enablingVideo ? 'Activation...' : 'Activer la visioconférence'}</Text>
                              </HStack>
                            </MenuItem>
                          )}
                          {isEditable && (
                            <MenuItem
                              value="delete"
                              onClick={() => setIsDeleteDialogOpen(true)}
                              color="red.600"
                              _hover={{ bg: 'red.50' }}
                            >
                              <HStack gap={2}>
                                <Trash2 size={16} />
                                <Text>Supprimer</Text>
                              </HStack>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </MenuRoot>
                  )}
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    aria-label="Fermer"
                  >
                    <X size={18} />
                  </IconButton>
                </HStack>
              </Flex>
            </Drawer.Header>

            {/* Body */}
            <Drawer.Body p={0}>
              {isLoading ? (
                <LoadingState message="Chargement de l'AG..." />
              ) : error ? (
                <ErrorState message="Erreur lors du chargement" />
              ) : meeting ? (
                <Flex direction="column" flex={1} minHeight={0}>
                  {/* Timestamps - toujours visibles */}
                  <MeetingTimestamps meeting={meeting} />

                  {/* Tabs */}
                  <Tabs.Root
                    value={activeTab}
                    onValueChange={(e) => e.value && setActiveTab(e.value)}
                    variant="enclosed"
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    minHeight={0}
                  >
                    {/* Tabs header */}
                    <Box px={6} pt={4}>
                      <Tabs.List>
                      <Tabs.Trigger value="info">
                        <HStack gap={1}>
                          <Info size={14} />
                          <Text>Informations</Text>
                        </HStack>
                      </Tabs.Trigger>
                      <Tabs.Trigger value="resolutions">
                        <HStack gap={1}>
                          <FileText size={14} />
                          <Text>Résolutions ({resolutions?.length || 0})</Text>
                        </HStack>
                      </Tabs.Trigger>
                      <Tabs.Trigger value="participants">
                        <HStack gap={1}>
                          <Users size={14} />
                          <Text>Participants</Text>
                        </HStack>
                      </Tabs.Trigger>
                    </Tabs.List>
                  </Box>

                  <Tabs.Content value="info" flex={1} overflow="auto" px={6} pt={4} pb={6}>
                    <MeetingInfoSection
                      meeting={meeting}
                      isEditable={isEditable}
                    />
                  </Tabs.Content>

                  <Tabs.Content value="resolutions" flex={1} overflow="auto" px={6} pt={4} pb={6}>
                    <MeetingResolutionsTab
                      meetingId={meeting.id}
                      residenceId={meeting.residence_id}
                      resolutions={(resolutions || []) as Resolution[]}
                      meetingStatus={meeting.status}
                    />
                  </Tabs.Content>

                  <Tabs.Content value="participants" flex={1} overflow="auto" px={6} pt={4} pb={6}>
                    <MeetingParticipantsTab
                      meeting={meeting}
                      resolutions={(resolutions || []) as Resolution[]}
                    />
                  </Tabs.Content>
                  </Tabs.Root>
                </Flex>
              ) : null}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Supprimer l'AG"
        message="Êtes-vous sûr de vouloir supprimer cette assemblée générale ? Cette action est irréversible."
        confirmLabel="Supprimer"
        colorPalette="red"
        icon={<Trash2 size={20} />}
        isLoading={deleteMeeting.isPending}
      />

      {/* Live AG Piloting Modal */}
      {meetingId && (
        <LiveAgPiloting
          meetingId={meetingId}
          isOpen={isPilotingOpen}
          onClose={() => setIsPilotingOpen(false)}
        />
      )}
    </>
  )
}

// Constantes pour le calcul des dates
const CONVOCATION_DAYS_BEFORE = 21
const PV_DAYS_AFTER = 15

// ===== Composant: Timestamps (dates calculées) =====
function MeetingTimestamps({ meeting }: { meeting: Meeting }) {
  const formatShortDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const convocationDeadline = meeting.meeting_date
    ? (() => {
        const date = new Date(meeting.meeting_date)
        date.setDate(date.getDate() - CONVOCATION_DAYS_BEFORE)
        return date
      })()
    : null

  const pvDeadline = meeting.meeting_date
    ? (() => {
        const date = new Date(meeting.meeting_date)
        date.setDate(date.getDate() + PV_DAYS_AFTER)
        return date
      })()
    : null

  return (
    <HStack
      gap={4}
      px={6}
      py={3}
      bg="gray.50"
      borderBottomWidth="1px"
      borderColor="gray.200"
      flexWrap="wrap"
    >
      <HStack gap={2} flex={1} minW="180px">
        <Box color="purple.500">
          <Clock size={16} />
        </Box>
        <VStack align="flex-start" gap={0}>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            Convocation au plus tard
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="purple.700">
            {convocationDeadline ? formatShortDate(convocationDeadline) : '-'}
          </Text>
        </VStack>
      </HStack>

      <HStack gap={2} flex={1} minW="180px">
        <Box color="orange.500">
          <FileText size={16} />
        </Box>
        <VStack align="flex-start" gap={0}>
          <Text fontSize="xs" color="gray.600" fontWeight="medium">
            Envoi PV au plus tard
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="orange.700">
            {pvDeadline ? formatShortDate(pvDeadline) : '-'}
          </Text>
        </VStack>
      </HStack>
    </HStack>
  )
}

// ===== Section: Informations (en haut du drawer) =====
interface MeetingInfoSectionProps {
  meeting: Meeting
  isEditable: boolean
}

function MeetingInfoSection({ meeting, isEditable }: MeetingInfoSectionProps) {
  const updateMeeting = useUpdateMeeting()
  const { data: videoRoom, isLoading: videoRoomLoading } = useVideoRoom(meeting.id)
  const createVideoRoom = useCreateVideoRoom()
  const deleteVideoRoomMutation = useDeleteVideoRoom()
  const [isCopied, setIsCopied] = React.useState(false)

  // Récupérer les données de l'utilisateur connecté et de l'agence pour la visio
  const { data: authData } = useAuth()
  const { data: agency } = useAgency()

  // Options pour le type d'AG
  const meetingTypeOptions = Object.entries(MEETING_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }))

  const handleFieldUpdate = async (fieldName: string, value: string | number) => {
    try {
      await updateMeeting.mutateAsync({
        id: meeting.id,
        data: { [fieldName]: value || null },
      })
      toaster.create({
        title: 'Mis à jour',
        type: 'success',
      })
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de mettre à jour',
        type: 'error',
      })
    }
  }

  // Gestion de la visioconférence
  const handleCreateVideoRoom = async () => {
    if (!meeting.meeting_date) {
      toaster.create({
        title: 'Date requise',
        description: 'Veuillez définir une date pour l\'AG avant de créer la room visio.',
        type: 'warning',
      })
      return
    }

    // Récupérer l'API key depuis l'environnement
    const apiKey = import.meta.env.VITE_DAILY_CO_API_KEY || import.meta.env.DAILY_CO_API_KEY
    if (!apiKey) {
      toaster.create({
        title: 'Configuration manquante',
        description: 'La clé API Daily.co n\'est pas configurée.',
        type: 'error',
      })
      return
    }

    try {
      await createVideoRoom.mutateAsync({
        meetingId: meeting.id,
        meetingDate: meeting.meeting_date,
        startTime: meeting.start_time ?? '14:00',
        meetingType: meeting.meeting_type ?? 'ORDINARY',
        agencySlug: agency?.slug || 'kopro',
        residenceKey: meeting.residence?.residence_key || meeting.residence?.id?.substring(0, 6) || 'RES',
        expectedParticipants: 50,
        apiKey,
      })
      toaster.create({
        title: 'Room créée',
        description: 'La room de visioconférence a été créée avec succès.',
        type: 'success',
      })
    } catch (error) {
      console.error('Erreur création room:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de créer la room visio.',
        type: 'error',
      })
    }
  }

  const handleCopyVideoLink = () => {
    const url = videoRoom?.daily_room_url || meeting.video_room_url
    if (url) {
      navigator.clipboard.writeText(url)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
      toaster.create({
        title: 'Lien copié',
        type: 'success',
      })
    }
  }

  const [joiningAsHost, setJoiningAsHost] = React.useState(false)

  const handleJoinAsHost = async () => {
    if (!videoRoom) return

    const apiKey = import.meta.env.VITE_DAILY_CO_API_KEY || import.meta.env.DAILY_CO_API_KEY
    if (!apiKey) {
      toaster.create({
        title: 'Configuration manquante',
        description: 'La clé API Daily.co n\'est pas configurée.',
        type: 'error',
      })
      return
    }

    setJoiningAsHost(true)
    try {
      // Nom de l'animateur (gestionnaire connecté)
      const hostName = authData?.profile
        ? `${authData.profile.first_name || ''} ${authData.profile.last_name || ''}`.trim() || 'Animateur AG'
        : 'Animateur AG'

      const token = await createMeetingToken({
        roomName: videoRoom.daily_room_name,
        userName: hostName,
        isOwner: true,
        expiresAt: new Date(videoRoom.expires_at),
        apiKey,
      })

      const urlWithToken = buildRoomUrlWithToken(videoRoom.daily_room_url, token)
      window.open(urlWithToken, '_blank')
    } catch (error) {
      console.error('Erreur génération token:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de générer le lien d\'accès.',
        type: 'error',
      })
    } finally {
      setJoiningAsHost(false)
    }
  }

  const [disablingVideo, setDisablingVideo] = React.useState(false)
  const [closingRoom, setClosingRoom] = React.useState(false)

  // Désactiver la visioconférence (supprime la room si elle existe)
  const handleDisableVideo = async () => {
    const apiKey = import.meta.env.VITE_DAILY_CO_API_KEY

    setDisablingVideo(true)
    try {
      // Récupérer la room depuis la base si pas en cache local
      const { getVideoRoomByMeetingId } = await import('../services/dailyService')
      const roomToDelete = videoRoom || await getVideoRoomByMeetingId(meeting.id)

      // Si une room existe, la supprimer via le mutation hook (invalide le cache)
      if (roomToDelete) {
        if (!apiKey) {
          console.warn('⚠️ VITE_DAILY_CO_API_KEY non configurée - impossible de supprimer la room Daily.co')
          toaster.create({
            title: 'Attention',
            description: 'La room Daily.co n\'a pas pu être supprimée (clé API manquante). Elle expirera automatiquement.',
            type: 'warning',
          })
        } else {
          console.log('🗑️ Suppression de la room Daily.co:', roomToDelete.daily_room_name)
          await deleteVideoRoomMutation.mutateAsync({
            meetingId: meeting.id,
            roomName: roomToDelete.daily_room_name,
            apiKey,
          })
          console.log('✅ Room supprimée avec succès (DB + Daily.co)')
        }
      } else {
        console.log('ℹ️ Aucune room à supprimer')
      }

      // Désactiver video_enabled sur le meeting
      await updateMeeting.mutateAsync({
        id: meeting.id,
        data: { video_enabled: false, video_room_url: null } as any,
      })

      toaster.create({
        title: 'Visioconférence désactivée',
        type: 'success',
      })
    } catch (error) {
      console.error('Erreur désactivation visio:', error)
      toaster.create({
        title: 'Erreur',
        description: `Impossible de désactiver la visioconférence: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        type: 'error',
      })
    } finally {
      setDisablingVideo(false)
    }
  }

  // Clôturer la room (fin de l'AG)
  const handleCloseRoom = async () => {
    if (!videoRoom) return

    const apiKey = import.meta.env.VITE_DAILY_CO_API_KEY || import.meta.env.DAILY_CO_API_KEY
    if (!apiKey) {
      toaster.create({
        title: 'Configuration manquante',
        description: 'La clé API Daily.co n\'est pas configurée.',
        type: 'error',
      })
      return
    }

    setClosingRoom(true)
    try {
      await deleteVideoRoomMutation.mutateAsync({
        meetingId: meeting.id,
        roomName: videoRoom.daily_room_name,
        apiKey,
      })

      toaster.create({
        title: 'Room clôturée',
        description: 'La room de visioconférence a été fermée.',
        type: 'success',
      })
    } catch (error) {
      console.error('Erreur clôture room:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de clôturer la room.',
        type: 'error',
      })
    } finally {
      setClosingRoom(false)
    }
  }

  // Vérifier si on est dans la fenêtre d'accès (30min avant le début)
  const isWithinAccessWindow = React.useMemo(() => {
    if (!videoRoom) return false
    const now = new Date()
    const accessStart = new Date(videoRoom.access_start_at)
    const expires = new Date(videoRoom.expires_at)
    return now >= accessStart && now <= expires
  }, [videoRoom])

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Flex gap={4} align="stretch" direction={{ base: 'column', lg: meeting.video_enabled ? 'row' : 'column' }}>
      {/* Colonne 1: Informations générales */}
      <Box flex={meeting.video_enabled ? 1 : 'auto'} minW={0}>
        <Card.Root variant="outline" h="full">
          <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
            <HStack>
              <Calendar size={16} />
              <Heading size="sm">Informations de l'AG</Heading>
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack align="stretch" gap={4}>
              <EditableField
                label="Titre"
                value={meeting.title}
                fieldName="title"
                placeholder="Titre de l'AG"
                disabled={!isEditable}
                onUpdate={handleFieldUpdate}
              />

              <EditableSelectField
                label="Type d'AG"
                value={meeting.meeting_type}
                fieldName="meeting_type"
                options={meetingTypeOptions}
                disabled={!isEditable}
                onUpdate={handleFieldUpdate}
              />

              <HStack gap={4} align="flex-start">
                <Box flex={1}>
                  <EditableField
                    label="Date"
                    value={meeting.meeting_date?.substring(0, 10) || ''}
                    fieldName="meeting_date"
                    placeholder="AAAA-MM-JJ"
                    disabled={!isEditable}
                    onUpdate={handleFieldUpdate}
                  />
                </Box>
                <Box flex={1}>
                  <EditableField
                    label="Heure"
                    value={meeting.start_time?.substring(0, 5) || ''}
                    fieldName="start_time"
                    placeholder="HH:MM"
                    disabled={!isEditable}
                    onUpdate={handleFieldUpdate}
                  />
                </Box>
              </HStack>

              <EditableField
                label="Lieu"
                value={meeting.location || ''}
                fieldName="location"
                placeholder="Salle de réunion, visio..."
                disabled={!isEditable}
                onUpdate={handleFieldUpdate}
              />

              <EditableField
                label="Notes"
                value={meeting.notes || ''}
                fieldName="notes"
                placeholder="Notes internes..."
                disabled={!isEditable}
                onUpdate={handleFieldUpdate}
              />
            </VStack>
          </Card.Body>
        </Card.Root>
      </Box>

      {/* Colonne 2: Visioconférence (si activée) */}
      {meeting.video_enabled && (
        <Box flex={1} minW={0}>
          <Card.Root variant="outline" borderColor="brand.200" h="full">
            <Card.Header py={3} bg="brand.50" borderBottomWidth="1px" borderColor="brand.200">
              <HStack>
                <Video size={16} color="#065F46" />
                <Heading size="sm" color="brand.700">Visioconférence</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              {videoRoomLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" />
                </Flex>
              ) : videoRoom ? (
                <VStack align="stretch" gap={3}>
                  {/* Lien de la room */}
                  <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Lien de connexion
                    </Text>
                    <HStack
                      p={2}
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Text fontSize="sm" flex={1} lineClamp={1} color="brand.600">
                        {videoRoom.daily_room_url}
                      </Text>
                      <IconButton
                        aria-label="Copier le lien"
                        size="xs"
                        variant="ghost"
                        onClick={handleCopyVideoLink}
                      >
                        {isCopied ? <CheckCircle size={14} color="green" /> : <Copy size={14} />}
                      </IconButton>
                    </HStack>
                  </Box>

                  {/* Fenêtre d'accès */}
                  <HStack gap={4} flexWrap="wrap">
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.500">Ouverture</Text>
                      <Text fontSize="sm" fontWeight="medium" color="brand.700">
                        {formatDateTime(videoRoom.access_start_at)}
                      </Text>
                    </VStack>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.500">Expiration</Text>
                      <Text fontSize="sm" fontWeight="medium" color="orange.600">
                        {formatDateTime(videoRoom.expires_at)}
                      </Text>
                    </VStack>
                    <Badge colorPalette={videoRoom.status === 'active' ? 'green' : videoRoom.status === 'pending' ? 'blue' : 'gray'} variant="subtle">
                      {videoRoom.status === 'pending' && 'En attente'}
                      {videoRoom.status === 'active' && 'Active'}
                      {videoRoom.status === 'ended' && 'Terminée'}
                      {videoRoom.status === 'expired' && 'Expirée'}
                      {videoRoom.status === 'cancelled' && 'Annulée'}
                    </Badge>
                  </HStack>

                  {/* Actions selon l'état de l'AG */}
                  {meeting.status === 'CLOSED' ? (
                    // AG terminée : afficher stats et bouton clôturer
                    <VStack gap={3} mt={2} align="stretch">
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" mb={2} fontWeight="medium">
                          Statistiques de la session
                        </Text>
                        <VStack align="stretch" gap={1}>
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">Statut</Text>
                            <Badge colorPalette={videoRoom.status === 'ended' ? 'green' : 'gray'} variant="subtle" size="sm">
                              {videoRoom.status === 'ended' ? 'Terminée' : videoRoom.status}
                            </Badge>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">Participants max</Text>
                            <Text fontSize="sm" fontWeight="medium">{videoRoom.max_participants || '-'}</Text>
                          </HStack>
                        </VStack>
                      </Box>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        onClick={handleCloseRoom}
                        loading={closingRoom}
                      >
                        Clôturer la room Daily.co
                      </Button>
                    </VStack>
                  ) : (
                    // AG non terminée : actions normales + désactiver
                    <VStack gap={2} mt={2} align="stretch">
                      {isWithinAccessWindow ? (
                        <Button
                          size="sm"
                          colorPalette="brand"
                          onClick={handleJoinAsHost}
                          loading={joiningAsHost}
                        >
                          <Video size={14} />
                          Rejoindre en tant qu'animateur
                        </Button>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          Le bouton de connexion sera disponible 30 min avant l'AG.
                        </Text>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={handleDisableVideo}
                        loading={disablingVideo}
                      >
                        <X size={14} />
                        Désactiver la visioconférence
                      </Button>
                    </VStack>
                  )}
                </VStack>
              ) : (
                // Pas de room : afficher info + créer + désactiver
                <VStack gap={3} py={2} align="stretch">
                  <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                    <Text fontSize="sm" color="blue.800">
                      La room de visioconférence sera <strong>automatiquement créée</strong> lors de l'envoi des convocations.
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    variant="outline"
                    colorPalette="brand"
                    onClick={handleCreateVideoRoom}
                    loading={createVideoRoom.isPending}
                  >
                    <Video size={14} />
                    Créer la room maintenant
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorPalette="red"
                    onClick={handleDisableVideo}
                    loading={disablingVideo}
                  >
                    <X size={14} />
                    Désactiver la visioconférence
                  </Button>
                </VStack>
              )}
            </Card.Body>
          </Card.Root>
        </Box>
      )}
    </Flex>
  )
}

// ===== Tab: Résolutions =====
interface MeetingResolutionsTabProps {
  meetingId: string
  residenceId: string
  resolutions: Resolution[]
  meetingStatus: string
}

// Composant pour une ligne de résolution draggable
interface SortableResolutionRowProps {
  resolution: Resolution
  isEditable: boolean
  onEdit: (resolution: Resolution) => void
  onDelete: (resolution: Resolution) => void
}

function SortableResolutionRow({
  resolution,
  isEditable,
  onEdit,
  onDelete,
}: SortableResolutionRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: resolution.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const getMajorityShortLabel = (majorityType: string | null | undefined): string => {
    if (!majorityType) return '-'
    switch (majorityType) {
      case 'simple': return 'Art. 24'
      case 'absolute': return 'Art. 25'
      case 'absolute_25_1': return 'Art. 25-1'
      case 'double': return 'Art. 26'
      case 'unanimity': return 'Unanimité'
      default: return majorityType
    }
  }

  return (
    <Table.Row
      ref={setNodeRef}
      style={style}
      _hover={{ bg: 'gray.50' }}
      bg={isDragging ? 'blue.50' : undefined}
    >
      {isEditable && (
        <Table.Cell w="40px">
          <Box
            {...attributes}
            {...listeners}
            cursor="grab"
            color="gray.400"
            _hover={{ color: 'gray.600' }}
          >
            <GripVertical size={16} />
          </Box>
        </Table.Cell>
      )}
      <Table.Cell w="50px">
        <Badge variant="outline" size="sm">
          {resolution.order_number || '-'}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        <VStack align="start" gap={0}>
          <Text fontWeight="medium" lineClamp={1}>
            {resolution.title}
          </Text>
          {resolution.description && (
            <Text fontSize="xs" color="gray.500" lineClamp={1}>
              {resolution.description}
            </Text>
          )}
        </VStack>
      </Table.Cell>
      <Table.Cell w="100px">
        <Badge variant="subtle" colorPalette="purple" size="sm">
          {getMajorityShortLabel(resolution.majority_type)}
        </Badge>
      </Table.Cell>
      <Table.Cell w="100px">
        <Badge
          colorPalette={RESOLUTION_STATUS_COLORS[resolution.status as keyof typeof RESOLUTION_STATUS_COLORS] || 'gray'}
          variant="subtle"
          size="sm"
        >
          {RESOLUTION_STATUS_LABELS[resolution.status as keyof typeof RESOLUTION_STATUS_LABELS] || resolution.status}
        </Badge>
      </Table.Cell>
      <Table.Cell w="80px">
        {resolution.status === 'CLOSED' && resolution.approved !== null ? (
          <Badge
            colorPalette={resolution.approved ? 'green' : 'red'}
            variant="solid"
            size="sm"
          >
            {resolution.approved ? 'Adoptée' : 'Rejetée'}
          </Badge>
        ) : isEditable ? (
          <HStack gap={1}>
            <IconButton
              aria-label="Modifier"
              size="xs"
              variant="ghost"
              onClick={() => onEdit(resolution)}
            >
              <Pencil size={14} />
            </IconButton>
            <IconButton
              aria-label="Supprimer"
              size="xs"
              variant="ghost"
              colorPalette="red"
              onClick={() => onDelete(resolution)}
            >
              <Trash2 size={14} />
            </IconButton>
          </HStack>
        ) : null}
      </Table.Cell>
    </Table.Row>
  )
}

function MeetingResolutionsTab({
  meetingId,
  residenceId,
  resolutions,
  meetingStatus,
}: MeetingResolutionsTabProps) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingResolution, setEditingResolution] = useState<Resolution | undefined>()
  const [deletingResolution, setDeletingResolution] = useState<Resolution | null>(null)

  const deleteResolution = useDeleteResolution()
  const reorderResolutions = useReorderResolutions()

  const isEditable = meetingStatus === 'SCHEDULED'

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = resolutions.findIndex((r) => r.id === active.id)
      const newIndex = resolutions.findIndex((r) => r.id === over.id)
      const newOrder = arrayMove(resolutions, oldIndex, newIndex)

      reorderResolutions.mutate({
        meetingId,
        orderedIds: newOrder.map((r) => r.id),
      })
    }
  }

  const handleAdd = () => {
    setEditingResolution(undefined)
    setIsFormOpen(true)
  }

  const handleEdit = (resolution: Resolution) => {
    setEditingResolution(resolution)
    setIsFormOpen(true)
  }

  const handleDelete = async () => {
    if (!deletingResolution) return

    try {
      await deleteResolution.mutateAsync({
        id: deletingResolution.id,
        meetingId,
      })
      toaster.create({
        title: 'Résolution supprimée',
        type: 'success',
      })
    } catch {
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de supprimer la résolution',
        type: 'error',
      })
    } finally {
      setDeletingResolution(null)
    }
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setEditingResolution(undefined)
  }

  return (
    <>
      <VStack gap={4} align="stretch">
        {/* Header avec bouton ajout */}
        <Flex justify="space-between" align="center">
          <Text fontWeight="medium" color="gray.600">
            {resolutions.length} résolution(s)
          </Text>
          {isEditable && (
            <Button size="sm" variant="outline" onClick={handleAdd}>
              <Plus size={14} />
              Ajouter
            </Button>
          )}
        </Flex>

        {resolutions.length === 0 ? (
          <Card.Root>
            <Card.Body p={6} textAlign="center">
              <Text color="gray.500">Aucune résolution pour cette AG</Text>
              {isEditable && (
                <Button mt={4} size="sm" colorPalette="brand" onClick={handleAdd}>
                  <Plus size={14} />
                  Ajouter une résolution
                </Button>
              )}
            </Card.Body>
          </Card.Root>
        ) : (
          <Card.Root>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row bg="gray.50">
                    {isEditable && <Table.ColumnHeader w="40px" />}
                    <Table.ColumnHeader w="50px">N°</Table.ColumnHeader>
                    <Table.ColumnHeader>Titre</Table.ColumnHeader>
                    <Table.ColumnHeader w="100px">Majorité</Table.ColumnHeader>
                    <Table.ColumnHeader w="100px">Statut</Table.ColumnHeader>
                    <Table.ColumnHeader w="80px">
                      {isEditable ? 'Actions' : 'Résultat'}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <SortableContext
                    items={resolutions.map((r) => r.id)}
                    strategy={verticalListSortingStrategy}
                    disabled={!isEditable}
                  >
                    {resolutions.map((resolution) => (
                      <SortableResolutionRow
                        key={resolution.id}
                        resolution={resolution}
                        isEditable={isEditable}
                        onEdit={handleEdit}
                        onDelete={setDeletingResolution}
                      />
                    ))}
                  </SortableContext>
                </Table.Body>
              </Table.Root>
            </DndContext>
          </Card.Root>
        )}

        {isEditable && resolutions.length > 0 && (
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Glissez-déposez les résolutions pour les réordonner
          </Text>
        )}
      </VStack>

      {/* Modal d'ajout/édition */}
      <ResolutionFormModal
        isOpen={isFormOpen}
        onClose={handleFormClose}
        meetingId={meetingId}
        residenceId={residenceId}
        resolution={editingResolution}
      />

      {/* Confirmation de suppression */}
      <ConfirmDialog
        isOpen={!!deletingResolution}
        onClose={() => setDeletingResolution(null)}
        onConfirm={handleDelete}
        title="Supprimer la résolution"
        message={`Êtes-vous sûr de vouloir supprimer la résolution "${deletingResolution?.title}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        colorPalette="red"
        icon={<Trash2 size={20} />}
        isLoading={deleteResolution.isPending}
      />
    </>
  )
}

// Type pour représenter un lot avec ses clés de répartition
interface RecipientUnit {
  id: string
  unitNumber: string
  distributionKeyIds: string[]
}

// Type pour représenter un participant (owner ou mandataire externe)
interface ConvocationRecipient {
  id: string
  type: 'owner' | 'external_contact'
  firstName: string
  lastName: string
  email: string | null
  address: string | null
  notificationPreference: 'ELECTRONIC' | 'PAPER'
  units: RecipientUnit[]
  isMandataire: boolean
  isIndivision: boolean
  indivisairesCount?: number
}

// ===== Tab: Participants =====
function MeetingParticipantsTab({ meeting, resolutions }: { meeting: Meeting; resolutions: Resolution[] }) {
  // Récupérer les données de l'utilisateur connecté et de l'agence
  const { data: authData } = useAuth()
  const { data: agency } = useAgency()

  // Récupérer tous les copropriétaires avec lots actifs dans cette résidence
  const { data: ownersData, isLoading: ownersLoading } = useOwnersPaginated({
    residenceId: meeting.residence_id,
    onlyWithActiveUnits: true,
    page: 1,
    pageSize: 200,
    sortField: 'last_name',
    sortDirection: 'asc',
  })

  // Récupérer les alertes d'indivision
  const { data: indivisionData, isLoading: indivisionLoading } = useIndivisionAlerts(meeting.residence_id)

  const owners = ownersData?.data || []
  // Les actions de convocation sont disponibles en SCHEDULED, CONVOCATION_READY et CONVOKED
  const canManageConvocations = meeting.status === 'SCHEDULED' || meeting.status === 'CONVOCATION_READY' || meeting.status === 'CONVOKED'
  const isDataLoading = ownersLoading || indivisionLoading

  // Construire la liste des destinataires de convocation
  // Cette liste fusionne les owners et les mandataires externes
  const recipients: ConvocationRecipient[] = React.useMemo(() => {
    if (!indivisionData) {
      // Pas encore de données d'indivision, afficher les owners tels quels
      return owners.map((owner) => ({
        id: owner.id,
        type: 'owner' as const,
        firstName: owner.first_name,
        lastName: owner.last_name,
        email: owner.email,
        address: owner.address || null,
        notificationPreference: owner.notification_preference as 'ELECTRONIC' | 'PAPER',
        units: owner.ownerships
          ?.filter((o) => !o.end_date && o.units?.unit_number)
          ?.map((o) => ({
            id: o.units?.id || '',
            unitNumber: o.units?.unit_number || '',
            distributionKeyIds: [],
          })) || [],
        isMandataire: false,
        isIndivision: false,
      }))
    }

    const result: ConvocationRecipient[] = []
    const processedOwnerIds = new Set<string>()

    // D'abord, ajouter les mandataires pour les lots en indivision avec mandataire
    for (const unit of indivisionData.unitsWithMandataire) {
      const unitData: RecipientUnit = {
        id: unit.id,
        unitNumber: unit.unit_number,
        distributionKeyIds: [], // Sera récupéré au moment de la génération VPC
      }

      if (unit.common_rep_contact_id && unit.common_rep_contact) {
        // Mandataire externe
        const existingIdx = result.findIndex(
          (r) => r.type === 'external_contact' && r.id === unit.common_rep_contact_id
        )
        if (existingIdx >= 0) {
          const existing = result[existingIdx]
          if (existing) {
            existing.units.push(unitData)
          }
        } else {
          result.push({
            id: unit.common_rep_contact_id,
            type: 'external_contact',
            firstName: unit.common_rep_contact.first_name,
            lastName: unit.common_rep_contact.last_name,
            email: unit.common_rep_contact.email,
            address: null,
            notificationPreference: (unit.common_rep_contact.notification_preference as 'ELECTRONIC' | 'PAPER') || 'ELECTRONIC',
            units: [unitData],
            isMandataire: true,
            isIndivision: true,
            indivisairesCount: unit.owners.length,
          })
        }
        // Marquer tous les indivisaires comme traités pour ce lot
        for (const owner of unit.owners) {
          processedOwnerIds.add(`${owner.id}-${unit.id}`)
        }
      } else if (unit.common_rep_owner_id && unit.common_rep_owner) {
        // Mandataire propriétaire
        const existingIdx = result.findIndex(
          (r) => r.type === 'owner' && r.id === unit.common_rep_owner_id
        )
        if (existingIdx >= 0) {
          const existing = result[existingIdx]
          if (existing) {
            if (!existing.units.some((u) => u.unitNumber === unit.unit_number)) {
              existing.units.push(unitData)
            }
            existing.isMandataire = true
            existing.isIndivision = true
            existing.indivisairesCount = (existing.indivisairesCount || 1) + unit.owners.length - 1
          }
        } else {
          const ownerData = owners.find((o) => o.id === unit.common_rep_owner_id)
          if (ownerData) {
            result.push({
              id: ownerData.id,
              type: 'owner',
              firstName: ownerData.first_name,
              lastName: ownerData.last_name,
              email: ownerData.email,
              address: ownerData.address || null,
              notificationPreference: ownerData.notification_preference as 'ELECTRONIC' | 'PAPER',
              units: [unitData],
              isMandataire: true,
              isIndivision: true,
              indivisairesCount: unit.owners.length,
            })
          }
        }
        // Marquer tous les indivisaires comme traités pour ce lot
        for (const owner of unit.owners) {
          processedOwnerIds.add(`${owner.id}-${unit.id}`)
        }
      }
    }

    // Ensuite, ajouter les owners pour les lots normaux (pas en indivision ou sans mandataire)
    for (const owner of owners) {
      const ownerUnits = owner.ownerships
        ?.filter((o) => !o.end_date && o.units?.unit_number)
        ?.map((o) => ({
          id: o.units?.id || '',
          unitId: o.unit_id,
          unitNumber: o.units?.unit_number || '',
        }))
        .filter((u) => u.unitNumber) || []

      // Filtrer les lots pour lesquels ce owner n'a pas déjà été traité comme indivisaire
      const unprocessedUnits = ownerUnits.filter(
        (u) => !processedOwnerIds.has(`${owner.id}-${u.unitId}`)
      )

      if (unprocessedUnits.length === 0) continue

      const existingIdx = result.findIndex((r) => r.type === 'owner' && r.id === owner.id)
      if (existingIdx >= 0) {
        // Ajouter les lots non traités à un owner existant
        const existing = result[existingIdx]
        if (existing) {
          for (const u of unprocessedUnits) {
            if (u.unitNumber && !existing.units.some((eu) => eu.unitNumber === u.unitNumber)) {
              existing.units.push({
                id: u.id,
                unitNumber: u.unitNumber,
                distributionKeyIds: [], // Sera récupéré au moment de la génération VPC
              })
            }
          }
        }
      } else {
        // Vérifier si cet owner est dans un lot en indivision sans mandataire
        const inIndivisionWithoutMandataire = indivisionData.unitsWithoutMandataire.some(
          (unit) => unit.owners.some((o) => o.id === owner.id)
        )

        result.push({
          id: owner.id,
          type: 'owner',
          firstName: owner.first_name,
          lastName: owner.last_name,
          email: owner.email,
          address: owner.address || null,
          notificationPreference: owner.notification_preference as 'ELECTRONIC' | 'PAPER',
          units: unprocessedUnits.map((u) => ({
            id: u.id,
            unitNumber: u.unitNumber,
            distributionKeyIds: [],
          })),
          isMandataire: false,
          isIndivision: inIndivisionWithoutMandataire,
        })
      }
    }

    // Trier par nom
    return result.sort((a, b) => a.lastName.localeCompare(b.lastName))
  }, [owners, indivisionData])

  // State pour le loading de génération VPC, Convocation et Pouvoir
  const [generatingVPCFor, setGeneratingVPCFor] = React.useState<string | null>(null)
  const [generatingConvocFor, setGeneratingConvocFor] = React.useState<string | null>(null)
  const [generatingPouvoirFor, setGeneratingPouvoirFor] = React.useState<string | null>(null)

  // Génération du formulaire VPC pour un participant
  const handleGenerateVPC = async (recipient: ConvocationRecipient) => {
    // Seuls les owners peuvent voter (pas les contacts externes qui sont mandataires)
    // Mais si c'est un mandataire externe, on génère le VPC au nom du mandataire
    if (resolutions.length === 0) {
      toaster.create({
        title: 'Impossible de générer le VPC',
        description: 'Aucune résolution n\'est définie pour cette AG.',
        type: 'warning',
      })
      return
    }

    setGeneratingVPCFor(recipient.id)

    try {
      // Récupérer les lots avec leurs clés de répartition
      const unitNumbers = recipient.units.map((u) => u.unitNumber)
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('id, unit_number')
        .eq('residence_id', meeting.residence_id)
        .in('unit_number', unitNumbers)

      if (unitsError) throw unitsError

      // Récupérer les clés de répartition pour ces lots
      const unitIds = (unitsData || []).map((u) => u.id)
      const { data: distributionKeysData, error: dkError } = await supabase
        .from('distribution_key_units')
        .select('unit_id, distribution_key_id')
        .in('unit_id', unitIds)

      if (dkError) throw dkError

      // Grouper les clés par lot
      const keysByUnit = new Map<string, string[]>()
      for (const dk of distributionKeysData || []) {
        if (!keysByUnit.has(dk.unit_id)) {
          keysByUnit.set(dk.unit_id, [])
        }
        keysByUnit.get(dk.unit_id)!.push(dk.distribution_key_id)
      }

      // Préparer les données du propriétaire/mandataire
      const ownerData: VPCOwnerData = {
        id: recipient.id,
        first_name: recipient.firstName,
        last_name: recipient.lastName,
        email: recipient.email,
        address: recipient.address,
      }

      // Préparer les lots avec leurs clés de répartition
      const units: VPCUnitData[] = (unitsData || []).map((unit) => ({
        id: unit.id,
        unit_number: unit.unit_number,
        distribution_key_ids: keysByUnit.get(unit.id) || [],
      }))

      // Préparer les données de l'agence
      const agencyData = {
        name: agency?.name || 'Syndic de copropriété',
        address: '',
        email: agency?.email || '',
      }

      // Préparer les données de la résidence
      const residenceData = {
        id: meeting.residence_id,
        name: meeting.residence?.name || 'Résidence',
        residence_key: meeting.residence?.residence_key,
      }

      await downloadVPCForm({
        meeting,
        owner: ownerData,
        units,
        resolutions,
        agency: agencyData,
        residence: residenceData,
      })

      toaster.create({
        title: 'Formulaire VPC généré',
        description: `Le formulaire de vote par correspondance pour ${recipient.lastName} ${recipient.firstName} a été téléchargé.`,
        type: 'success',
      })
    } catch (error) {
      console.error('Erreur lors de la génération du VPC:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de générer le formulaire VPC. Vérifiez que le template est disponible.',
        type: 'error',
      })
    } finally {
      setGeneratingVPCFor(null)
    }
  }

  // Génération de la lettre de convocation pour un participant
  const handleGenerateConvocation = async (recipient: ConvocationRecipient) => {
    setGeneratingConvocFor(recipient.id)

    try {
      // Nom complet du gestionnaire connecté
      const gestionnaireNom = authData?.profile
        ? `${authData.profile.first_name || ''} ${authData.profile.last_name || ''}`.trim() || 'Le Gestionnaire'
        : 'Le Gestionnaire'

      // Préparer les données du propriétaire/mandataire
      const ownerData: ConvocationOwnerData = {
        id: recipient.id,
        first_name: recipient.firstName,
        last_name: recipient.lastName,
        email: recipient.email,
        address_line1: recipient.address || undefined,
        address_line2: undefined,
        postal_code: undefined,
        city: undefined,
      }

      // Préparer les données de l'agence
      const agencyData: ConvocationAgencyData = {
        name: agency?.name || 'Syndic de copropriété',
        raison_sociale: agency?.name || 'Syndic de copropriété',
        address: '',
        telephone: agency?.phone || '',
        email: agency?.email || '',
        forme_juridique: '',
        capital: undefined,
        siret: '',
        rcs_ville: '',
        carte_pro: '',
        carte_pro_delivree_par: '',
        garantie_financiere: '',
        garantie_adresse: '',
        orias: '',
      }

      // Préparer les données de la résidence
      const residenceData: ConvocationResidenceData = {
        id: meeting.residence_id,
        name: meeting.residence?.name || 'Résidence',
        address: meeting.residence?.address || undefined,
        city: meeting.residence?.city || undefined,
        postal_code: meeting.residence?.postal_code || undefined,
      }

      // Préparer les données du gestionnaire
      const gestionnaireData: ConvocationGestionnaireData = {
        nom: gestionnaireNom,
        email: authData?.profile?.email || '',
      }

      // Préparer les lots
      const units = recipient.units.map((u) => ({
        id: u.id,
        unit_number: u.unitNumber,
      }))

      await downloadConvocationLetter({
        meeting,
        owner: ownerData,
        units,
        agency: agencyData,
        residence: residenceData,
        gestionnaire: gestionnaireData,
        resolutions,
        lieuEmission: meeting.residence?.city || undefined,
      })

      toaster.create({
        title: 'Convocation générée',
        description: `La lettre de convocation pour ${recipient.lastName} ${recipient.firstName} a été téléchargée.`,
        type: 'success',
      })
    } catch (error) {
      console.error('Erreur lors de la génération de la convocation:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de générer la convocation. Vérifiez que le template est disponible.',
        type: 'error',
      })
    } finally {
      setGeneratingConvocFor(null)
    }
  }

  // Génération du pouvoir/procuration pour un participant
  const handleGeneratePouvoir = async (recipient: ConvocationRecipient) => {
    setGeneratingPouvoirFor(recipient.id)

    try {
      // Récupérer les tantièmes des lots du propriétaire
      const unitIds = recipient.units.map((u) => u.id)
      let ownerTantiemes = 0

      if (unitIds.length > 0) {
        const { data: unitsData } = await supabase
          .from('units')
          .select('general_shares')
          .in('id', unitIds)

        if (unitsData) {
          ownerTantiemes = unitsData.reduce((sum, u) => sum + (u.general_shares || 0), 0)
        }
      }

      // Préparer les données du propriétaire
      const ownerData: PouvoirOwnerData = {
        id: recipient.id,
        first_name: recipient.firstName,
        last_name: recipient.lastName,
        email: recipient.email,
        address_line1: recipient.address || undefined,
        address_line2: undefined,
        postal_code: undefined,
        city: undefined,
        tantiemes: ownerTantiemes,
      }

      // Préparer les données de la résidence
      const residenceData: PouvoirResidenceData = {
        id: meeting.residence_id,
        name: meeting.residence?.name || 'Résidence',
        code: meeting.residence?.residence_key,
        address: meeting.residence?.address || undefined,
        city: meeting.residence?.city || undefined,
        postal_code: meeting.residence?.postal_code || undefined,
        tantiemes_total: meeting.residence?.total_shares || 10000,
      }

      await downloadPouvoir({
        meeting,
        owner: ownerData,
        residence: residenceData,
        lieuEmission: meeting.residence?.city || undefined,
      })

      toaster.create({
        title: 'Pouvoir généré',
        description: `Le formulaire de pouvoir pour ${recipient.lastName} ${recipient.firstName} a été téléchargé.`,
        type: 'success',
      })
    } catch (error) {
      console.error('Erreur lors de la génération du pouvoir:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de générer le pouvoir. Vérifiez que le template est disponible.',
        type: 'error',
      })
    } finally {
      setGeneratingPouvoirFor(null)
    }
  }

  // Mock actions (à implémenter)
  const handleGenerateConvocations = () => {
    toaster.create({
      title: 'Génération en cours',
      description: 'Les convocations sont en cours de génération (mock)',
      type: 'info',
    })
  }

  const handleSendConvocations = () => {
    toaster.create({
      title: 'Envoi en cours',
      description: 'Les convocations sont en cours d\'envoi (mock)',
      type: 'info',
    })
  }

  const handleMarkAsSent = (_recipientId: string) => {
    toaster.create({
      title: 'Marqué comme envoyé',
      description: 'La convocation a été marquée comme envoyée (mock)',
      type: 'success',
    })
  }

  const handleAddProxy = (_recipientId: string, recipientName: string) => {
    toaster.create({
      title: 'Ajout de pouvoir',
      description: `Ajout d'un pouvoir pour ${recipientName} (mock)`,
      type: 'info',
    })
  }

  const handleAddCorrespondenceVote = (_recipientId: string, recipientName: string) => {
    toaster.create({
      title: 'Vote par correspondance',
      description: `Saisie du vote par correspondance de ${recipientName} (mock)`,
      type: 'info',
    })
  }

  // Calcul stats
  const electronicCount = recipients.filter(r => r.notificationPreference === 'ELECTRONIC').length
  const paperCount = recipients.filter(r => r.notificationPreference === 'PAPER').length
  const externalContactsCount = recipients.filter(r => r.type === 'external_contact').length

  return (
    <VStack gap={4} align="stretch">
      {/* Alerte indivision sans mandataire */}
      {indivisionData?.hasAlerts && (
        <Alert.Root status="warning">
          <Alert.Indicator>
            <AlertTriangle />
          </Alert.Indicator>
          <Alert.Content>
            <Alert.Title fontWeight="semibold">
              {indivisionData.unitsWithoutMandataire.length} lot(s) en indivision sans mandataire
            </Alert.Title>
            <Alert.Description>
              Les lots suivants sont en indivision et n'ont pas de mandataire commun désigné : {' '}
              <Text as="span" fontWeight="medium">
                {indivisionData.unitsWithoutMandataire.map(u => u.unit_number).join(', ')}
              </Text>
              . Un mandataire doit être désigné pour recevoir les convocations.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      {/* Header avec stats et actions */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
        <HStack gap={4}>
          <Text fontWeight="medium" color="gray.600">
            {recipients.length} destinataire(s)
          </Text>
          <HStack gap={2}>
            <Badge colorPalette="blue" variant="subtle" size="sm">
              <Mail size={12} style={{ marginRight: 4 }} />
              {electronicCount} LRE
            </Badge>
            <Badge colorPalette="orange" variant="subtle" size="sm">
              <FileText size={12} style={{ marginRight: 4 }} />
              {paperCount} Papier
            </Badge>
            {externalContactsCount > 0 && (
              <Badge colorPalette="purple" variant="subtle" size="sm">
                <Users size={12} style={{ marginRight: 4 }} />
                {externalContactsCount} Mandataire(s) externe(s)
              </Badge>
            )}
          </HStack>
        </HStack>

        {/* Actions convocations */}
        {canManageConvocations && (
          <MenuRoot>
            <MenuTrigger asChild>
              <Button size="sm" variant="outline" colorPalette="brand">
                <Send size={14} />
                Convocations
                <ChevronDown size={14} />
              </Button>
            </MenuTrigger>
            <MenuPositioner>
              <MenuContent minW="220px" zIndex={9999}>
                <MenuItem value="generate" onClick={handleGenerateConvocations}>
                  <HStack gap={2}>
                    <FileText size={16} />
                    <Text>Générer les convocations</Text>
                  </HStack>
                </MenuItem>
                <MenuItem value="send" onClick={handleSendConvocations}>
                  <HStack gap={2}>
                    <Send size={16} />
                    <Text>Envoyer les convocations</Text>
                  </HStack>
                </MenuItem>
              </MenuContent>
            </MenuPositioner>
          </MenuRoot>
        )}
      </Flex>

      {isDataLoading ? (
        <Flex justify="center" py={8}>
          <Spinner size="sm" />
        </Flex>
      ) : recipients.length === 0 ? (
        <Card.Root>
          <Card.Body p={6} textAlign="center">
            <Text color="gray.500">Aucun copropriétaire avec lot actif dans cette résidence</Text>
          </Card.Body>
        </Card.Root>
      ) : (
        <Card.Root>
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row bg="gray.50">
                <Table.ColumnHeader>Destinataire</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Lot(s)</Table.ColumnHeader>
                <Table.ColumnHeader w="80px">Notif.</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Convocation</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Pouvoir</Table.ColumnHeader>
                <Table.ColumnHeader w="60px">VPC</Table.ColumnHeader>
                <Table.ColumnHeader w="50px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {recipients.map((recipient) => {
                const unitNumbers = recipient.units.map((u) => u.unitNumber).join(', ') || '-'

                // Mock convocation status - en réalité viendrait de meeting_participants
                const convocationStatus: ConvocationStatus = 'pending'

                // Mock proxy et VPC
                const hasProxy = false
                const hasVPC = false

                return (
                  <Table.Row key={`${recipient.type}-${recipient.id}`} _hover={{ bg: 'gray.50' }}>
                    <Table.Cell>
                      <VStack align="start" gap={1}>
                        <HStack gap={2}>
                          <Text fontWeight="medium" fontSize="sm">
                            {recipient.lastName} {recipient.firstName}
                          </Text>
                          {recipient.type === 'external_contact' && (
                            <Badge colorPalette="purple" variant="subtle" size="sm">
                              Externe
                            </Badge>
                          )}
                          {recipient.isMandataire && (
                            <Badge colorPalette="teal" variant="subtle" size="sm">
                              Mandataire{recipient.indivisairesCount ? ` (${recipient.indivisairesCount})` : ''}
                            </Badge>
                          )}
                          {recipient.isIndivision && !recipient.isMandataire && (
                            <Badge colorPalette="yellow" variant="subtle" size="sm">
                              Indivision
                            </Badge>
                          )}
                        </HStack>
                        {recipient.email && (
                          <Text fontSize="xs" color="gray.500" lineClamp={1}>
                            {recipient.email}
                          </Text>
                        )}
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{unitNumbers}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={recipient.notificationPreference === 'ELECTRONIC' ? 'blue' : 'orange'}
                        variant="subtle"
                        size="sm"
                      >
                        {recipient.notificationPreference === 'ELECTRONIC' ? 'LRE' : 'Papier'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={CONVOCATION_STATUS_COLORS[convocationStatus]}
                        variant="subtle"
                        size="sm"
                      >
                        {CONVOCATION_STATUS_LABELS[convocationStatus]}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {hasProxy ? (
                        <Badge colorPalette="purple" variant="subtle" size="sm">
                          M. Dupont
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.400">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {hasVPC ? (
                        <Badge colorPalette="green" variant="solid" size="sm">
                          <Vote size={12} />
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.400">-</Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {canManageConvocations && (
                        <MenuRoot positioning={{ placement: 'bottom-end' }}>
                          <MenuTrigger asChild>
                            <IconButton variant="ghost" size="xs" aria-label="Actions">
                              <MoreVertical size={14} />
                            </IconButton>
                          </MenuTrigger>
                          <MenuPositioner>
                            <MenuContent minW="220px" zIndex={9999}>
                              <MenuItem
                                value="generate-convocation"
                                onClick={() => handleGenerateConvocation(recipient)}
                                disabled={generatingConvocFor === recipient.id}
                              >
                                <HStack gap={2}>
                                  {generatingConvocFor === recipient.id ? (
                                    <Spinner size="xs" />
                                  ) : (
                                    <FileDown size={14} />
                                  )}
                                  <Text>
                                    {generatingConvocFor === recipient.id ? 'Génération...' : 'Prévisualiser convocation'}
                                  </Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem
                                value="generate-vpc"
                                onClick={() => handleGenerateVPC(recipient)}
                                disabled={generatingVPCFor === recipient.id}
                              >
                                <HStack gap={2}>
                                  {generatingVPCFor === recipient.id ? (
                                    <Spinner size="xs" />
                                  ) : (
                                    <Download size={14} />
                                  )}
                                  <Text>
                                    {generatingVPCFor === recipient.id ? 'Génération...' : 'Générer formulaire VPC'}
                                  </Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem
                                value="generate-pouvoir"
                                onClick={() => handleGeneratePouvoir(recipient)}
                                disabled={generatingPouvoirFor === recipient.id}
                              >
                                <HStack gap={2}>
                                  {generatingPouvoirFor === recipient.id ? (
                                    <Spinner size="xs" />
                                  ) : (
                                    <UserCheck size={14} />
                                  )}
                                  <Text>
                                    {generatingPouvoirFor === recipient.id ? 'Génération...' : 'Générer pouvoir'}
                                  </Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem
                                value="mark-sent"
                                onClick={() => handleMarkAsSent(recipient.id)}
                              >
                                <HStack gap={2}>
                                  <CheckCircle size={14} />
                                  <Text>Marquer comme envoyé</Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem
                                value="add-proxy"
                                onClick={() => handleAddProxy(recipient.id, `${recipient.firstName} ${recipient.lastName}`)}
                              >
                                <HStack gap={2}>
                                  <FileSignature size={14} />
                                  <Text>Ajouter un pouvoir</Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem
                                value="add-vpc"
                                onClick={() => handleAddCorrespondenceVote(recipient.id, `${recipient.firstName} ${recipient.lastName}`)}
                              >
                                <HStack gap={2}>
                                  <Vote size={14} />
                                  <Text>Saisir vote par correspondance</Text>
                                </HStack>
                              </MenuItem>
                            </MenuContent>
                          </MenuPositioner>
                        </MenuRoot>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Card.Root>
      )}
    </VStack>
  )
}
